import { QueryPartData } from '../../../advanced-search/filters/base/entities/query-part-data.entity';
import { QueryPartStrategy } from '../../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../../advanced-search/filters/base/entities/query-part.class';
import { rangeValueToString } from '../../../saved-queries/helpers/convert-structured-query.helper';
import { EmptyStructuredQuery } from '../entities/empty-structured-query.constant';
import { LogicalOperand, Operand } from '../entities/operand.entity';
import {
    AdvancedSearchGroup,
    AdvancedSearchTerm,
    Bucket,
    Facet,
    StructuredQuery
} from '../entities/structured-query.entity';

export function GenerateStructuredQueryFromGlobalSearchTerm(globalValues: string[]): StructuredQuery {
    return GenerateStructuredQueryFromAdvancedSearchTerms([], globalValues);
}

export function GenerateStructuredQueryFromAdvancedSearchTerm(
    dottedKey: string,
    values: string[],
    operand: Operand = Operand.Include
): StructuredQuery {
    return GenerateStructuredQueryFromAdvancedSearchTerms([
        {
            DottedKey: dottedKey,
            Values: values,
            Operand: operand
        }
    ]);
}

export function GenerateStructuredQueryFromAdvancedSearchTerms(
    terms: AdvancedSearchTerm[],
    globalValues: string[] = []
): StructuredQuery {
    // Add all Include and Excludes to a single AND operation group
    const andQueryParts: AdvancedSearchTerm[] = terms.filter(term => term.Operand !== Operand.Should);

    // Create OR operation group per Should qp
    const orGroups: AdvancedSearchGroup[] = terms
        .filter(term => term.Operand === Operand.Should)
        .map(term => ({
            LogicalOperand: LogicalOperand.Or,
            Terms: term.Values?.map(value => ({
                Operand: Operand.Include,
                DottedKey: term.DottedKey,
                Values: [value]
            }))
        }));

    const structuredQuery: StructuredQuery = {
        ...EmptyStructuredQuery,
        Global: globalValues,
        AdvancedSearch: {
            LogicalOperand: LogicalOperand.And,
            Groups: [
                {
                    LogicalOperand: LogicalOperand.And,
                    Terms: andQueryParts
                },
                ...orGroups
            ]
        }
    };
    return structuredQuery;
}

export function GenerateStructuredQueryFromFieldSearch(fieldQueryParts: QueryPartData[]): StructuredQuery {
    return {
        ...EmptyStructuredQuery,
        Fields: fieldQueryParts
    };
}

export function QueryPartsToStructuredQuery(queryParts: QueryPart[]): StructuredQuery {
    const globalValues: string[] = QueryPartsToGlobal(queryParts);
    const terms = QueryPartsToAdvancedSearchTerms(queryParts);
    return GenerateStructuredQueryFromAdvancedSearchTerms(terms, globalValues);
}

export function QueryPartsToGlobal(queryParts: QueryPart[]): string[] {
    return queryParts.find(qp => qp.field === 'q')?.values ?? [];
}

export function QueryPartsToAdvancedSearchTerms(queryParts: QueryPart[]): AdvancedSearchTerm[] {
    return queryParts.filter(qp => qp.field !== 'q').map(qp => qp.toAdvancedSearchTerm());
}

export function MergeStructuredQueries(main: StructuredQuery, toMergeIn: StructuredQuery): StructuredQuery {
    return {
        Global: [...main.Global, ...toMergeIn.Global],
        Semantic: main.Semantic ? main.Semantic : toMergeIn.Semantic,
        FullText: main.FullText ? main.FullText : toMergeIn.FullText,
        AdvancedSearch: {
            ...main.AdvancedSearch,
            Groups: [...main.AdvancedSearch.Groups, ...toMergeIn.AdvancedSearch.Groups]
        },
        Facets: [...main.Facets, ...toMergeIn.Facets],
        Fields: [...main.Fields, ...toMergeIn.Fields],
        ColumnFilters: [...main.ColumnFilters, ...toMergeIn.ColumnFilters],
        Predefined: [...(main.Predefined ?? []), ...(toMergeIn.Predefined ?? [])]
    };
}

/**
 * Should not be used!
 * Only used for legacy conversions in DH
 * Can not contain the 'multilayered' advanced search. Just flattens it
 * @deprecated
 * @param structuredQuery
 * @returns
 */
export function StructuredQueryToQueryParts(structuredQuery: StructuredQuery): QueryPart[] {
    const qp = [];

    if (structuredQuery.Global.length > 0) {
        const q = new QueryPart({
            field: 'q',
            values: structuredQuery.Global
        });
        qp.push(q);
    }

    if (structuredQuery.Semantic) {
        const sq = new QueryPart({
            field: 'sq',
            values: [structuredQuery.Semantic]
        });
        qp.push(sq);
    }

    if (structuredQuery.Facets.length > 0) {
        qp.push(...FacetsToQueryParts(structuredQuery.Facets));
    }

    structuredQuery.AdvancedSearch.Groups.forEach(group =>
        group.Terms?.forEach(term => {
            const operand: QueryPartStrategy =
                term.Operand === Operand.Exclude
                    ? QueryPartStrategy.MatchNone
                    : term.Operand === Operand.Include
                      ? QueryPartStrategy.MatchAll
                      : QueryPartStrategy.MatchAtLeastOne;

            let values: string[] | undefined = term.Values;

            if (term.RangeValues) {
                values = term.RangeValues.map(value => rangeValueToString(value));
            }

            const termQp: QueryPart = new QueryPart({
                field: term.DottedKey,
                values: values,
                strategy: operand
            });

            qp.push(termQp);
        })
    );

    return qp;
}

/**
 * @deprecated
 * To be removed after DAV splitoff
 * */
export function FacetsToQueryParts(facets: Facet[]): QueryPart[] {
    const facetQueryParts: QueryPart[] = [];

    facets.forEach(facet => {
        const key = facet.DottedKey;

        const matchNoneValues: string[] = [];
        const matchAllValues: string[] = [];
        const matchAtLeastOneValues: string[] = [];

        // Convert Buckets and RangeBuckets to values per strategy
        const buckets = getBucketsOfFacet(facet);
        buckets.forEach(bucket => {
            let strategy: QueryPartStrategy;

            switch (bucket.Operand) {
                case Operand.Exclude:
                    strategy = QueryPartStrategy.MatchNone;
                    break;
                case Operand.Include:
                    strategy = QueryPartStrategy.MatchAll;
                    break;
                default:
                    strategy = QueryPartStrategy.MatchAtLeastOne;
            }

            const facetQueryPart: QueryPartData = {
                field: facet.DottedKey,
                values: [bucket.Value],
                strategy
            };

            facetQueryParts.push(new QueryPart(facetQueryPart));
        });

        if (matchAtLeastOneValues.length > 0) {
            facetQueryParts.push(
                new QueryPart({
                    field: key,
                    values: matchAtLeastOneValues,
                    strategy: QueryPartStrategy.MatchAtLeastOne
                })
            );
        }

        if (matchAllValues.length > 0) {
            facetQueryParts.push(
                new QueryPart({
                    field: key,
                    values: matchAllValues,
                    strategy: QueryPartStrategy.MatchAll
                })
            );
        }

        if (matchNoneValues.length > 0) {
            facetQueryParts.push(
                new QueryPart({
                    field: key,
                    values: matchNoneValues,
                    strategy: QueryPartStrategy.MatchNone
                })
            );
        }
    });
    return facetQueryParts;
}

function getBucketsOfFacet(facet: Facet): Bucket[] {
    if (facet.Buckets && facet.Buckets.length !== 0) {
        return facet.Buckets;
    }
    if (facet.RangeBuckets && facet.RangeBuckets.length !== 0) {
        return facet.RangeBuckets.map(rangeBucket => ({
            Operand: rangeBucket.Operand,
            Value: rangeValueToString(rangeBucket.RangeValue)
        }));
    }
    return [];
}

export function HasFiltersSet(query: StructuredQuery): boolean {
    return !!query.AdvancedSearch?.Groups.length || !!query.Facets.length || !!query.Semantic || !!query.Global.length;
}
